import clsx from 'clsx';
import Image from 'components/ui/Image';
import config from 'config';
import { isAsset, isGqlImage, isMedia } from 'types/Utilities';
import type { NormalizedSlide, Slide } from 'types/Utilities';

type ItemProps = {
  slide: Slide;
  isGrid?: boolean;
  isMobile?: boolean;
  isThumbnail?: boolean;
  variant: string;
};

const DefaultItem = ({
  slide,
  isGrid = false,
  isThumbnail = false,
  variant,
}: ItemProps): JSX.Element => {
  const { imageFallbackSize } = config.defaults;

  /**
   * Adjust the slide to match the given required object structure
   */
  const normalizeSlide = (slide: Slide): NormalizedSlide => {
    if (isAsset(slide)) {
      const { fields } = slide;
      const { details, url } = fields.file;

      return {
        width: details?.image?.width || imageFallbackSize,
        height: details?.image?.height || imageFallbackSize,
        description: fields.description,
        url,
      };
    }

    if (isMedia(slide)) {
      const { media } = slide;

      return {
        width: media.width || imageFallbackSize,
        height: media.height || imageFallbackSize,
        description: media.description,
        url: media.url,
      };
    }

    return slide as unknown as NormalizedSlide;
  };

  const normalizedSlide = normalizeSlide(slide);
  const additionalProps = {
    width: (normalizedSlide.width as number) || imageFallbackSize,
    height: (normalizedSlide.height as number) || imageFallbackSize,
  };

  return (
    <div>
      <div
        className={clsx(
          'relative flex justify-center shrink-0 mx-auto rounded-md overflow-hidden cursor-pointer items-center',
          !isGrid && !isThumbnail && 'md:w-9/12 carousel-rounded-image',
          !isThumbnail ? 'lg:h-auto' : 'h-8 lg:h-10',
        )}
      >
        <Image
          src={normalizedSlide.url}
          alt={normalizedSlide.description}
          className={clsx(
            'object-contain',
            isGrid ? 'bg-bluishGrey rounded-md' : 'md:rounded-md',
          )}
          contentful
          {...additionalProps}
        />
      </div>
      {!isGrid ? (
        <p
          className={clsx(
            'text-center pt-1 md:hidden px-4',
            variant !== 'alternate' && 'text-white md:mb-20',
          )}
          dangerouslySetInnerHTML={{
            __html: normalizedSlide.description,
          }}
        />
      ) : null}
    </div>
  );
};

type Props = {
  isGrid?: boolean;
  isMobile?: boolean;
  isThumbnail?: boolean;
  onImageClick: (i: number) => void;
  slides: Slide[];
  variant: string;
};

const Default = ({
  isGrid = false,
  onImageClick,
  slides,
  isThumbnail = false,
  variant,
}: Props): JSX.Element => {
  if (isGrid) {
    return (
      <div className="flex flex-wrap box-border mx-auto before:box-inherit after:box-inherit -mb-3">
        {slides.map((slide, i) => {
          const prevIndex = i > 0 ? i - 1 : i;

          let colSpan = false;
          let isVertical = false;
          let width = 0;
          let height = 0;
          let prevWidth = 0;
          let prevHeight = 0;

          if (isAsset(slide)) {
            const { image } = slide.fields.file.details;
            // eslint-disable-next-line
            // @ts-ignore
            const { image: prevImage } = slides[prevIndex].fields.file.details;

            // eslint-disable-next-line
            // @ts-ignore
            width = parseFloat(image?.width);
            // eslint-disable-next-line
            // @ts-ignore
            height = parseFloat(image?.height);
            // eslint-disable-next-line
            // @ts-ignore
            prevWidth = parseFloat(prevImage?.width);
            // eslint-disable-next-line
            // @ts-ignore
            prevHeight = parseFloat(prevImage?.height);
          }

          if (isMedia(slide)) {
            const { media: image } = slide;
            // eslint-disable-next-line
            // @ts-ignore
            const { media: prevImage } = slides[prevIndex];

            // eslint-disable-next-line
            // @ts-ignore
            width = parseFloat(image.width);
            // eslint-disable-next-line
            // @ts-ignore
            height = parseFloat(image.height);
            // eslint-disable-next-line
            // @ts-ignore
            prevWidth = parseFloat(prevImage.width);
            // eslint-disable-next-line
            // @ts-ignore
            prevHeight = parseFloat(prevImage.height);
          }

          if (isGqlImage(slide)) {
            // eslint-disable-next-line
            // @ts-ignore
            width = parseFloat(slide.width);
            // eslint-disable-next-line
            // @ts-ignore
            height = parseFloat(slide.height);
            // eslint-disable-next-line
            // @ts-ignore
            prevWidth = parseFloat(slides[prevIndex].width);
            // eslint-disable-next-line
            // @ts-ignore
            prevHeight = parseFloat(slides[prevIndex].height);
          }

          if (width < height) {
            isVertical = true;
          }

          if (width > height && prevWidth > prevHeight) {
            colSpan = true;
          }

          const getStyle = (): object => {
            if (colSpan) {
              return { flexBasis: '100%', paddingLeft: '', paddingRight: '' };
            }

            if (isVertical) {
              return { flexBasis: '30%', paddingLeft: '', paddingRight: '' };
            }

            return { flexBasis: '70%', paddingLeft: '15px', paddingRight: '' };
          };

          return (
            <div
              key={i}
              onClick={() => {
                onImageClick(i);
              }}
              className={clsx(isVertical ? 'vertical' : 'horizontal', 'mb-3')}
              style={getStyle()}
            >
              <DefaultItem
                slide={slide}
                isGrid={isGrid}
                isThumbnail={isThumbnail}
                variant={variant}
              />
            </div>
          );
        })}
      </div>
    );
  }

  return <></>;
};

Default.Item = DefaultItem;
export default Default;
